import { IAction, KEEP_THE_SAME, StateStatus } from 'redux/utils/common';
import {
  IAgent,
  IClientDetailsData,
  IRole,
  ICustomersMiniProfileData,
  ICustomerTagListOutput,
} from 'services/api/client';
import * as Types from './types';
import { IParalistByGroupData } from 'services/api/paralist';
import { INote } from 'constants/notes';
import { ISpecificationAttributeListOutput } from 'services/api/specificationattribute';

interface IAttribute {
  value: string;
}
export interface IState {
  clientDetails: {
    status: StateStatus | null;
    data: IClientDetailsData | null;
  };
  clientMiniProfiles: {
    status: StateStatus | null;
    data: ICustomersMiniProfileData | null;
  };
  agents: {
    status: StateStatus | null;
    data: Array<IAgent> | null;
  };
  statusList: {
    status: StateStatus | null;
    data: IParalistByGroupData | null;
  };
  readinessLevelList: {
    status: StateStatus | null;
    data: IParalistByGroupData | null;
  };
  roles: {
    status: StateStatus | null;
    data: Array<IRole> | null;
  };
  updateStatus: StateStatus | null;
  updateDetailsStatus: StateStatus | null;

  clientDetailNote: {
    createNote: StateStatus | null | undefined;
    clientNotes: {
      data: Array<INote>;
      status: StateStatus | null | undefined;
    };
    deleteNoteStatus: StateStatus | null | undefined;
  };
  customerTagList: {
    status: StateStatus | null;
    data: ICustomerTagListOutput | null;
  };
  specificationAttributeList: {
    status: StateStatus | null;
    data: ISpecificationAttributeListOutput | null;
  };
  clientAttributeCompanyName: {
    status: StateStatus | null;
    data: IAttribute[] | null;
  };
  clientAttributeJobPosition: {
    status: StateStatus | null;
    data: IAttribute[] | null;
  };
  clientAttributeIndustry: {
    status: StateStatus | null;
    data: IAttribute[] | null;
  };
  clientAttributeFoundUsOn: {
    status: StateStatus | null;
    data: IAttribute[] | null;
  };
}

const initialState: IState = {
  clientDetails: {
    status: null,
    data: null,
  },
  clientMiniProfiles: {
    status: null,
    data: null,
  },
  agents: {
    status: null,
    data: null,
  },
  statusList: {
    status: null,
    data: null,
  },
  readinessLevelList: {
    status: null,
    data: null,
  },
  roles: {
    status: null,
    data: null,
  },
  updateStatus: null,
  updateDetailsStatus: null,
  clientDetailNote: {
    createNote: null,
    clientNotes: {
      data: [],
      status: null,
    },
    deleteNoteStatus: null,
  },
  customerTagList: {
    status: null,
    data: null,
  },
  specificationAttributeList: {
    status: null,
    data: null,
  },
  clientAttributeCompanyName: {
    status: null,
    data: null,
  },
  clientAttributeJobPosition: {
    status: null,
    data: null,
  },
  clientAttributeIndustry: {
    status: null,
    data: null,
  },
  clientAttributeFoundUsOn: {
    status: null,
    data: null,
  },
};

const ClientListReducer = (
  state: IState = initialState,
  action: IAction
): IState => {
  switch (action.type) {
    case Types.SET_CLIENT_DETAILS:
      return {
        ...state,
        clientDetails: {
          ...state.clientDetails,
          status: action.status || null,
          data:
            action.data === KEEP_THE_SAME
              ? state.clientDetails.data
              : action.data,
        },
      };
    case Types.SET_AGENTS:
      return {
        ...state,
        agents: {
          ...state.agents,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_CLIENTS_MINI_PROFILES:
      return {
        ...state,
        clientMiniProfiles: {
          ...state.clientMiniProfiles,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.UPDATE_STATUS:
      return {
        ...state,
        updateStatus: action.status || null,
      };
    case Types.FETCH_ROLES:
      return {
        ...state,
        roles: {
          ...state.roles,
          status: action.status || null,
          data: action.data,
        },
      };
    case Types.SET_STATUS_LIST:
      return {
        ...state,
        statusList: {
          ...state.statusList,
          status: action.status || null,
          data:
            action.data === KEEP_THE_SAME ? state.statusList.data : action.data,
        },
      };
    case Types.SET_READINESS_LEVEL_LIST:
      return {
        ...state,
        readinessLevelList: {
          ...state.readinessLevelList,
          status: action.status || null,
          data:
            action.data === KEEP_THE_SAME
              ? state.readinessLevelList.data
              : action.data,
        },
      };
    case Types.UPDATE_DETAILS:
      return {
        ...state,
        updateDetailsStatus: action.status || null,
      };
    case Types.CLIENT_DETAIL_CREATE_NOTE:
      return {
        ...state,
        clientDetailNote: {
          ...state.clientDetailNote,
          createNote: action.status,
        },
      };
    case Types.SET_CLIENT_DETAIL_NOTES:
      return {
        ...state,
        clientDetailNote: {
          ...state.clientDetailNote,
          clientNotes: {
            data:
              action.data === KEEP_THE_SAME
                ? state.clientDetailNote.clientNotes.data
                : action.data,
            status: action.status,
          },
        },
      };
    case Types.SET_CUSTOMER_TAG_LIST:
      return {
        ...state,
        customerTagList: {
          ...state.customerTagList,
          status: action.status || null,
          data:
            action.data === KEEP_THE_SAME
              ? state.customerTagList.data
              : action.data,
        },
      };
    case Types.SET_SPECIFICATIONATTRIBUTE_LIST:
      return {
        ...state,
        specificationAttributeList: {
          ...state.specificationAttributeList,
          status: action.status || null,
          data:
            action.data === KEEP_THE_SAME
              ? state.specificationAttributeList.data
              : action.data,
        },
      };
    case Types.SET_CLIENT_ATTRIBUTE_FOUND_US_ON:
      return {
        ...state,
        clientAttributeFoundUsOn: {
          ...state.clientAttributeFoundUsOn,
          status: action.status || null,
          data:
            action.data === KEEP_THE_SAME
              ? state.clientAttributeFoundUsOn.data
              : action.data,
        },
      };
    case Types.SET_CLIENT_ATTRIBUTE_INDUSTRY:
      return {
        ...state,
        clientAttributeIndustry: {
          ...state.clientAttributeIndustry,
          status: action.status || null,
          data:
            action.data === KEEP_THE_SAME
              ? state.clientAttributeIndustry.data
              : action.data,
        },
      };
    case Types.SET_CLIENT_ATTRIBUTE_JOB_POSITION:
      return {
        ...state,
        clientAttributeJobPosition: {
          ...state.clientAttributeJobPosition,
          status: action.status || null,
          data:
            action.data === KEEP_THE_SAME
              ? state.clientAttributeJobPosition.data
              : action.data,
        },
      };
    case Types.SET_CLIENT_ATTRIBUTE_COMPANY_NAME:
      return {
        ...state,
        clientAttributeCompanyName: {
          ...state.clientAttributeCompanyName,
          status: action.status || null,
          data:
            action.data === KEEP_THE_SAME
              ? state.clientAttributeCompanyName.data
              : action.data,
        },
      };
    default:
      return {
        ...state,
      };
  }
};

export default ClientListReducer;
